import React from 'react';
import { Typography, Box, MenuItem, Stack, TextField, Select as MuiSelect, Chip, Button, CircularProgress, Tooltip } from "@mui/material";
import { FormControl, InputLabel } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Grid from "@mui/material/Grid2";
import AlertDialog from "../../Dialog.js";
import LockIcon from "@mui/icons-material/Lock";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";
import { PRE_EXISTING_CONDITIONS } from "../../constants/preExistingConditions.js";
import SearchableMultiSelect from "../../components/SearchableMultiSelect.js";
import { DSM5_CONDITIONS } from "../../constants/dsm5Conditions.js";
import { countryCodes } from "../../constants/countryCodes.js";

// Shared presentation: callers supply data and existing event handlers.
export default function PatientProfilePanel({ DEFAULT_PHONE_CODE, editedPatient, fieldSubmitted, getCaseOwnerName, handleInputChange, isEditing, isMindvaultLinked, hasMindvaultAccount = null, isMindvaultLinkLoading = false, contactReadOnly = isMindvaultLinked, isPrefillingClinical = false, linkedCaseOwnerNames, onPrefillClinical, openDialog, patient, setAlert, setEditedPatient, setOpenDialog, teamMembersData }) {

      const fieldLabelSx = { fontWeight: 500, color: '#374151', fontSize: '0.75rem', mb: 0.25 };
      const fieldValueSx = { color: '#111827', fontSize: '0.8rem' };
      const cardSx = {
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        p: 2.5,
        height: '100%',
      };
      const sectionTitleSx = {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: '#111827',
        pb: 1.5,
        mb: 2,
        borderBottom: '1px solid #E5E7EB',
      };

      return (
        <Box sx={{ pb: 4 }}>
          <Grid container spacing={2.5}>
            {/* ── Personal Information Card ── */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={cardSx}>
                <Typography sx={sectionTitleSx}>Personal Information</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 2fr) minmax(0, 1fr)' }, gap: 2.5, alignItems: 'start' }}>
                <Box sx={{ minWidth: 0 }}>
                {isEditing ? (
                  <Stack spacing={2.5}>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <TextField fullWidth label="First Name" size="small" value={editedPatient.firstName || ''} onChange={handleInputChange('firstName')} />
                      </Grid>
                      <Grid size={6}>
                        <TextField fullWidth label="Last Name" size="small" value={editedPatient.lastName || ''} onChange={handleInputChange('lastName')} />
                      </Grid>
                    </Grid>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Date of Birth"
                        value={editedPatient.birth_date ? new Date(editedPatient.birth_date) : null}
                        onChange={(newValue) => {
                          if (newValue) {
                            const today = new Date();
                            today.setHours(23, 59, 59, 999);
                            if (newValue > today) {
                              setAlert('Date of Birth cannot be in the future.', 'error');
                              setEditedPatient({ ...editedPatient, birth_date: patient.birth_date ? new Date(patient.birth_date) : null });
                              return;
                            }
                            setEditedPatient({ ...editedPatient, birth_date: new Date(newValue) });
                          } else {
                            setEditedPatient({ ...editedPatient, birth_date: null });
                          }
                        }}
                        views={['year', 'month', 'day']}
                        maxDate={new Date()}
                        sx={{ width: '100%' }}
                        slotProps={{ textField: { fullWidth: true, size: 'small' }, field: { clearable: true } }}
                      />
                    </LocalizationProvider>
                    <FormControl fullWidth size="small">
                      <InputLabel id="gender-label">Gender</InputLabel>
                      <MuiSelect labelId="gender-label" label="Gender" value={editedPatient.gender || ''} onChange={handleInputChange('gender')}>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                      </MuiSelect>
                    </FormControl>
                    <TextField fullWidth label="HKID Number" size="small" value={editedPatient.hkid || ''} onChange={handleInputChange('hkid')} />
                    {isMindvaultLinked && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '8px', backgroundColor: '#F0FDFA', border: '1px solid #99E6E4' }}>
                        <LockIcon sx={{ fontSize: 14, color: '#0D9488', flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ color: '#0D9488', lineHeight: 1.4 }}>
                          Phone number and email are managed by the linked MindVault account and cannot be edited here.
                        </Typography>
                      </Box>
                    )}
                    <Grid container spacing={1}>
                      <Grid size={5}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="phone-code-label" shrink>Region</InputLabel>
                          <MuiSelect labelId="phone-code-label" value={editedPatient.phone_code || (contactReadOnly ? '' : DEFAULT_PHONE_CODE)} onChange={handleInputChange('phone_code')} label="Region" disabled={!!contactReadOnly}>
                            {countryCodes.map((c) => (
                              <MenuItem key={c.code} value={c.code}>{c.name} (+{c.code})</MenuItem>
                            ))}
                          </MuiSelect>
                        </FormControl>
                      </Grid>
                      <Grid size={7}>
                        <TextField fullWidth label="Phone Number" size="small" value={editedPatient.phoneNumber || ''} onChange={handleInputChange('phoneNumber')} disabled={!!contactReadOnly} />
                      </Grid>
                    </Grid>
                    <TextField
                      fullWidth
                      label="Email"
                      size="small"
                      value={editedPatient.email || ''}
                      onChange={handleInputChange('email')}
                      disabled={!!contactReadOnly}
                      helperText={isMindvaultLinked
                        ? ''
                        : 'Optional — needed only to link this client\'s MindVault account.'}
                      // InputProps={{
                      //   endAdornment: (
                      //     <InputAdornment position="end">
                      //       <Tooltip title="Use the same email registered with the MindVault app to automatically link and sync patient data." arrow placement="top">
                      //         <InfoIcon sx={{ color: '#29C0BB', cursor: 'pointer', fontSize: 18 }} />
                      //       </Tooltip>
                      //     </InputAdornment>
                      //   ),
                      // }}
                    />
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <TextField fullWidth label="Emergency Contact Person" size="small" value={editedPatient.emergency_contact_person || ''} onChange={handleInputChange('emergency_contact_person')} />
                      </Grid>
                      <Grid size={6}>
                        <TextField fullWidth label="Emergency Contact" size="small" value={editedPatient.emergency_contact || ''} onChange={handleInputChange('emergency_contact')} />
                      </Grid>
                    </Grid>
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    <Box>
                      <Typography sx={fieldLabelSx}>Name</Typography>
                      <Typography sx={fieldValueSx}>{patient.firstName} {patient.lastName}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={fieldLabelSx}>Date of Birth</Typography>
                      <Typography sx={fieldValueSx}>
                        {editedPatient.birth_date ? format(new Date(editedPatient.birth_date), 'dd MMM, yyyy') : 'Not specified'}
                        {editedPatient.age ? ` (${editedPatient.age}y)` : ''}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={fieldLabelSx}>Gender</Typography>
                      <Typography sx={fieldValueSx}>{editedPatient.gender || 'Not specified'}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={fieldLabelSx}>HKID</Typography>
                      <Typography sx={fieldValueSx}>{editedPatient.hkid || 'Not specified'}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={fieldLabelSx}>Phone</Typography>
                      <Typography sx={fieldValueSx}>{editedPatient.phoneNumber ? `+${editedPatient.phone_code || '852'} ${editedPatient.phoneNumber}` : 'Not specified'}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={fieldLabelSx}>Email</Typography>
                      <Typography sx={fieldValueSx}>{editedPatient.email || 'Not specified'}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={fieldLabelSx}>Join Date</Typography>
                      <Typography sx={fieldValueSx}>{editedPatient.join_date ? format(new Date(editedPatient.join_date), 'PP') : 'Not specified'}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={fieldLabelSx}>Emergency Contact</Typography>
                      <Typography sx={fieldValueSx}>
                        {editedPatient.emergency_contact_person || editedPatient.emergency_contact
                          ? `${editedPatient.emergency_contact_person || ''}${editedPatient.emergency_contact_person && editedPatient.emergency_contact ? ' - ' : ''}${editedPatient.emergency_contact || ''}`
                          : 'Not specified'
                        }
                      </Typography>
                    </Box>
                  </Stack>
                )}
                </Box>
                <Box role="group" aria-label="MindVault client">
                  <Typography sx={fieldLabelSx}>MindVault client</Typography>
                  <Typography sx={fieldValueSx}>
                    {isMindvaultLinkLoading ? 'Checking…' : hasMindvaultAccount === true ? 'Yes' : hasMindvaultAccount === false ? 'No' : 'Unknown'}
                  </Typography>
                </Box>
                </Box>
              </Box>
            </Grid>

            {/* ── Professional & Background Card ── */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={cardSx}>
                <Typography sx={sectionTitleSx}>Professional & Background</Typography>
                {isEditing ? (
                  <Stack spacing={2.5}>
                    {isMindvaultLinked ? (
                      <Box>
                        <Typography sx={fieldLabelSx}>Case Owner</Typography>
                        <Typography sx={fieldValueSx}>
                          {linkedCaseOwnerNames.join(', ') || 'Not specified'}
                        </Typography>
                      </Box>
                    ) : (
                      <SearchableMultiSelect
                        label="Case Owners"
                        options={teamMembersData}
                        value={editedPatient.case_owner || []}
                        onChange={handleInputChange('case_owner')}
                        error={fieldSubmitted && (!editedPatient.case_owner || editedPatient.case_owner.length === 0)}
                        helperText={fieldSubmitted && (!editedPatient.case_owner || editedPatient.case_owner.length === 0) ? 'Please select at least one case owner' : ''}
                        required
                        getOptionLabel={(option) => getCaseOwnerName(option)}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        chipColor="#29C0BB"
                        placeholder="Search or select case owners..."
                      />
                    )}
                    <FormControl fullWidth size="small">
                      <InputLabel id="status-label">Status</InputLabel>
                      <MuiSelect labelId="status-label" label="Status" value={editedPatient.status || ''} onChange={handleInputChange('status')}>
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Inactive">Inactive</MenuItem>
                      </MuiSelect>
                    </FormControl>
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    <Box>
                      <Typography sx={fieldLabelSx}>Case Owner</Typography>
                      <Typography sx={fieldValueSx}>
                        {isMindvaultLinked 
                          ? (linkedCaseOwnerNames.join(', ') || 'Not specified')
                          : ((editedPatient.case_owner || []).map(getCaseOwnerName).filter(Boolean).join(', ') || 'Not specified')
                        }
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={fieldLabelSx}>Status</Typography>
                      <Chip
                        label={editedPatient.status || 'Active'}
                        size="small"
                        sx={{
                          mt: 0.5,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          backgroundColor: editedPatient.status === 'Inactive' ? '#FEF2F2' : '#F0FDF4',
                          color: editedPatient.status === 'Inactive' ? '#DC2626' : '#16A34A',
                        }}
                      />
                    </Box>
                  </Stack>
                )}
              </Box>
            </Grid>

            {/* ── Clinical Information Card (full width) ── */}
            <Grid size={12}>
              <Box sx={cardSx}>
                <Box sx={{ ...sectionTitleSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>Clinical Information</Typography>
                  {isEditing && onPrefillClinical && (
                    <Tooltip title="Suggest diagnosis, conditions, presenting concerns and remarks from this client's reports and transcripts. Empty fields are filled in; nothing is saved until you click Save Changes.">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={onPrefillClinical}
                          disabled={isPrefillingClinical}
                          startIcon={isPrefillingClinical ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            borderColor: '#29C0BB',
                            color: '#0F766E',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            py: 0.25,
                            '&:hover': { borderColor: '#1ca8a4', backgroundColor: 'rgba(41,192,187,0.08)' },
                          }}
                        >
                          {isPrefillingClinical ? 'Prefilling…' : 'Prefill with AI'}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                {isEditing ? (
                  <Grid container spacing={2.5}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <SearchableMultiSelect
                        label="Diagnosis (DSM-5)"
                        options={DSM5_CONDITIONS}
                        value={editedPatient.diagnosis || []}
                        onChange={handleInputChange('diagnosis')}
                        chipColor="#9C27B0"
                        placeholder="Search or select diagnosis"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <SearchableMultiSelect
                        label="Pre-existing Conditions"
                        options={PRE_EXISTING_CONDITIONS}
                        value={editedPatient.preExistingConditions || []}
                        onChange={handleInputChange('preExistingConditions')}
                        chipColor="#FF6B6B"
                        placeholder="Search or select conditions..."
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label="Presenting Concerns"
                        size="small"
                        placeholder="Presenting Concerns"
                        value={editedPatient.presenting_concerns || ''}
                        onChange={handleInputChange('presenting_concerns')}
                        multiline
                        minRows={2}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label="Additional Remarks"
                        size="small"
                        placeholder="Enter remarks"
                        value={editedPatient.remarks || ''}
                        onChange={handleInputChange('remarks')}
                        multiline
                        minRows={2}
                      />
                    </Grid>
                  </Grid>
                ) : (
                <Grid container spacing={4}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box>
                      <Typography sx={fieldLabelSx}>Diagnosis (DSM-5)</Typography>
                      <Typography sx={fieldValueSx}>
                        {editedPatient.diagnosis && editedPatient.diagnosis.length > 0 
                          ? editedPatient.diagnosis.join(', ') 
                          : 'None specified'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box>
                      <Typography sx={fieldLabelSx}>Pre-existing Conditions</Typography>
                      <Typography sx={fieldValueSx}>
                        {editedPatient.preExistingConditions && editedPatient.preExistingConditions.length > 0 
                          ? editedPatient.preExistingConditions.join(', ') 
                          : 'None specified'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box>
                      <Typography sx={fieldLabelSx}>Presenting Concerns</Typography>
                      <Typography sx={{ ...fieldValueSx, whiteSpace: 'pre-wrap' }}>
                        {editedPatient.presenting_concerns || 'Not specified'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box>
                      <Typography sx={fieldLabelSx}>Additional Remarks</Typography>
                      <Typography sx={{ ...fieldValueSx, whiteSpace: 'pre-wrap' }}>
                        {editedPatient.remarks || 'Not specified'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                )}
              </Box>
            </Grid>
          </Grid>

          {openDialog && (
            <AlertDialog data={{open: openDialog.open, content: openDialog.content, confirmAction: openDialog.confirmAction, setOpen: setOpenDialog}} />
          )}
        </Box>
      )
    
}
